import { useCallback, useEffect, useState } from 'react';
import { useStore } from 'react-redux';
import { useCurrentOrgName } from '@rsos/sinatra/src/selectors';
import {
  DEFAULT_SETTINGS,
  DiscrepancySettings,
  fetchDiscrepancySettings,
  updateDiscrepancySettings,
} from './api';

export interface UseDiscrepancySettingsResult {
  /** Whether the settings are currently being fetched */
  loading: boolean;
  /** Whether the settings are currently being saved */
  saving: boolean;
  /** The current discrepancy settings */
  settings: DiscrepancySettings;
  /** Error message if fetch/save failed, or null if no error */
  error: string | null;
  /** Update settings locally (does not save to server) */
  setSettings: React.Dispatch<React.SetStateAction<DiscrepancySettings>>;
  /** Save current settings to server */
  save: () => Promise<boolean>;
}

/**
 * React hook to fetch and manage discrepancy settings for the current organization.
 * Automatically fetches settings when the component mounts or when the org changes.
 */
export function useDiscrepancySettings(): UseDiscrepancySettingsResult {
  const orgName = useCurrentOrgName();
  const store = useStore();
  const [settings, setSettings] =
    useState<DiscrepancySettings>(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);

    if (!orgName) {
      setLoading(false);
      setError('No organization name available');
      setSettings(DEFAULT_SETTINGS);
      return;
    }

    fetchDiscrepancySettings(orgName, store)
      .then(fetchedSettings => {
        setSettings(fetchedSettings);
        setLoading(false);
      })
      .catch((err: unknown) => {
        const errorMessage = err instanceof Error ? err.message : String(err);
        setError(errorMessage);
        setSettings(DEFAULT_SETTINGS);
        setLoading(false);
      });
  }, [orgName, store]);

  const save = useCallback(async (): Promise<boolean> => {
    if (!orgName) {
      setError('No organization name available');
      return false;
    }

    setSaving(true);
    setError(null);

    try {
      await updateDiscrepancySettings(orgName, settings, store);
      setSaving(false);
      return true;
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : String(err);
      setError(errorMessage);
      setSaving(false);
      return false;
    }
  }, [orgName, settings, store]);

  return {
    settings,
    setSettings,
    loading,
    saving,
    error,
    save,
  };
}
